import { createFileRoute } from "@tanstack/react-router";
import { PHOTOGRAPHER } from "@/content/foto";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { FotoQuoteForm } from "@/components/site/foto/FotoQuoteForm";
import { PhotoMasonry } from "@/components/site/foto/PhotoMasonry";
import { coverPhotoFor, photosBy } from "@/content/photo-gallery";
import {
  aeoGeo,
  breadcrumbs,
  faqPage,
  imageGallery,
  serviceSchema,
  person,
  speakable,
} from "@/lib/seo/schema";

const COVER = coverPhotoFor("studiofotografering");

const PATH = "/foto/studiofotografering";
const TITLE = "Studiofotografering — porträtt och produkt i studio | Fenny";
const DESCRIPTION =
  "Studiofotografering med kontrollerat ljus och ren bakgrund. Porträtt, produkt och kampanj — i vår studio eller mobil studio hos er. Offert per uppdrag.";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad är fördelen med studio jämfört med fotografering på plats?",
    a: "Kontroll. Ljuset är detsamma från första till sista bilden, bakgrunden är ren och resultatet går att återskapa exakt vid en kompletterande fotografering månader senare. Det gör studio till förstahandsvalet när en hel organisation eller ett helt sortiment ska se enhetligt ut.",
  },
  {
    q: "Kan ni bygga studio i våra lokaler?",
    a: "Ja. Vi tar med bakgrund, blixtar och softboxar och bygger en mobil studio på ungefär tre gånger tre meter. Ett vanligt konferensrum räcker, och riggningen tar cirka 45 minuter.",
  },
  {
    q: "Vilka bakgrunder kan vi välja?",
    a: "Vit, grå och svart är standard och fungerar i de flesta sammanhang. Vi kan också matcha en varumärkesfärg om ni har ett manér som kräver det — då provfotograferar vi mot färgprov innan dagen.",
  },
  {
    q: "Går det att frilägga bilderna efteråt?",
    a: "Ja. Fotograferar vi mot vit eller ljusgrå botten kan bilderna friläggas utan halo-kanter och användas mot vilken bakgrund som helst i layout och annons.",
  },
  {
    q: "Kan vi filma i samma studio samma dag?",
    a: "Ja. Ljussättningen fungerar för både stillbild och rörligt, och vi bemannar med både fotograf och filmteam när uppdraget kräver det.",
  },
];

export const Route = createFileRoute("/foto/studiofotografering")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Studiofotografering — Fenny Foto" },
      { property: "og:description", content: "Kontrollerat ljus, ren bakgrund och hög produktionstakt." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
    ],
    links: [{ rel: "canonical", href: `https://fenny.se${PATH}` }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({
            path: PATH,
            name: TITLE,
            description: DESCRIPTION,
            question: "När lönar det sig att fotografera i studio i stället för på plats?",
            answer:
              "Studiofotografering lönar sig när bilderna ska vara enhetliga över tid, när bakgrunden ska vara ren och när många personer eller artiklar ska igenom samma uppsättning. I studio är ljuset konstant, tempot högre och resultatet repeterbart, vilket gör att kompletteringar månader senare matchar originalserien. Fenny fotograferar i studio och bygger även mobil studio hos er. Offert per uppdrag. Kontakt: Fenny, hey@fenny.se.",
            speakableSelectors: ["[data-quick-answer]"],
          })) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Studiofotografering",
            path: PATH,
            serviceType: "Studiofotografering",
            description: "Studiofotografering av porträtt, produkt och kampanj — i studio eller mobil studio hos kund.",
            areaServed: ["Stockholm", "Göteborg", "Malmö", "Uppsala", "Västerås", "Sverige"],
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          imageGallery({
            name: "Studiofotografering — exempel",
            path: PATH,
            description: "Exempel på studiofotografering av Fenny för Fenny.",
            images: photosBy("studio"),
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          person({
            name: PHOTOGRAPHER.name,
            jobTitle: PHOTOGRAPHER.role,
            email: PHOTOGRAPHER.email,
            path: PATH,
            description: PHOTOGRAPHER.bio,
            knowsAbout: [
              "Studiofotografering",
              "Fotostudio",
              "Packshot",
              "Företagsporträtt",
              "Fotograf Stockholm",
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Foto", path: "/foto" },
            { name: "Studiofotografering", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <LandingPage
      heroImage={{ src: COVER.src, alt: COVER.alt }}
      eyebrow="Studiofotografering"
      title={<>Studio: samma ljus i dag, om ett halvår och om två år.</>}
      lede="Kontrollerat ljus och ren bakgrund gör bilderna enhetliga och möjliga att komplettera i efterhand. Vi fotograferar i studio eller bygger en mobil studio i era lokaler — riggningen tar 45 minuter och kräver ungefär tre gånger tre meter."
      tldr={
        <p>
          Studiofotografering ger <strong>identiskt ljus över tid</strong>, ren
          bakgrund och bilder som går att <strong>frilägga</strong>. Mobil studio
          kräver <strong>cirka 3×3 meter</strong> och <strong>45 minuters</strong>{" "}
          riggning. Offert per uppdrag.
        </p>
      }
      ctaPrimary={{ label: "Begär offert", to: "#offert" }}
      ctaSecondary={{ label: "Företagsporträtt", to: "/foto/foretagsportratt" }}
      trackingId="foto_studiofotografering"
      stills={false}
      videos={false}
      afterHero={
        <div className="py-12">
          <PhotoMasonry
            photos={photosBy("studio")}
            filterable={false}
            initial={9}
            eyebrow="Studio"
            heading="Ljus som går att upprepa"
          />
        </div>
      }
      bullets={[
        { title: "Återskapbart", body: "Vi dokumenterar uppsättningen så att nästa pass matchar exakt." },
        { title: "Högt tempo", body: "Ingen omriggning mellan personer eller artiklar — allt går snabbare." },
        { title: "Frilägg utan halo", body: "Ren botten ger rena urklipp för layout, annons och e-handel." },
      ]}
      sections={[
        {
          id: "vad-passar-i-studio",
          heading: "Vad som passar i studio",
          body: (
            <p>
              Porträtt av hela organisationen, produktserier som ska se
              likadana ut, kampanjbilder som kräver exakt ljus och allt som ska
              friläggas. Behövs miljö och sammanhang är fotografering på plats
              ett bättre val — ofta kombinerar vi: studio för porträtt,
              plats för miljöbilder. Se{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foto/foretagsfotografering">
                företagsfotografering
              </a>
              .
            </p>
          ),
        },
        {
          id: "mobil-studio",
          heading: "Mobil studio hos er",
          body: (
            <p>
              Vi tar med bakgrundssystem, blixtar, softboxar och reflektorer.
              Kravet är en yta på ungefär tre gånger tre meter, ett eluttag och
              helst takhöjd över 2,4 meter. Vi riggar på 45 minuter och plockar
              ned på 30.
            </p>
          ),
        },
      ]}
      faq={FAQ}
      riskReducers
      closing={{
        title: "Behöver ni studiobilder?",
        body: "Berätta vad som ska fotograferas så föreslår vi upplägg och fast pris.",
        primary: { label: "Begär offert", to: "#offert" },
      }}
      contactSlot={<FotoQuoteForm location="foto_studiofotografering" defaultAssignment="studiofoto" />}
    />
  );
}
